import type { PropsWithChildren, ReactNode } from 'react'
import { View } from 'react-native'

import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import { useUserByUpdateParametersTokenQuery } from '@/src/features/user/services/useUserByUpdateParametersTokenQuery'
import { TEST_ID } from '@/src/general/constants/testId'
import { filterSearchParams } from '@/src/general/utils/filterSearchParams'

import { SettingsScreensProtectedNavigationContent } from './SettingsScreensProtectedNavigationContent'

export const SettingsScreensProtectedNavigation = ({
  children,
  searchParams,
}: PropsWithChildren<TSearchParamsParam>): ReactNode => {
  const { updateParametersToken } = filterSearchParams(searchParams)

  const isLoggedIn = getIsLoggedIn()
  const { data: me, isLoading } = useMeQuery()

  const {
    data: user,
    isPending: isUserLoading,
    isError: isUserError,
  } = useUserByUpdateParametersTokenQuery({
    token: updateParametersToken || '',
    enabled: !!updateParametersToken,
  })

  if ((isUserLoading || isLoading) && updateParametersToken) return <View testID={TEST_ID.SETTINGS.LOADING} />

  const isIdMatch = !!me && !!user && me.id === user.id

  const doesUserIdMismatch = isLoggedIn && updateParametersToken && !isIdMatch
  const invalidUpdateParametersToken = isLoggedIn && updateParametersToken && isUserError
  const userIdMatch = isLoggedIn && updateParametersToken && isIdMatch
  const isGuestInfoInvalid = !isLoggedIn && updateParametersToken && isUserError

  const shouldClearToken = !!(
    (userIdMatch || isGuestInfoInvalid || doesUserIdMismatch || invalidUpdateParametersToken) &&
    updateParametersToken
  )

  return (
    <SettingsScreensProtectedNavigationContent
      shouldClearToken={shouldClearToken}
      invalidUpdateParametersToken={!!invalidUpdateParametersToken}
      doesUserIdMismatch={!!doesUserIdMismatch}
      updateParametersToken={updateParametersToken}
      userIdMatch={!!userIdMatch}
      isGuestInfoInvalid={!!isGuestInfoInvalid}
      searchParams={searchParams}
    >
      {children}
    </SettingsScreensProtectedNavigationContent>
  )
}
